// Catálogo de módulos do cadastro de usuários: como as chaves de permissão aparecem na tela.
// As dependências entre permissões vêm da API (/Permissao/Dependencias), fonte única com o backend.

import { CARDS_DO_DASHBOARD } from './permissoes-token';

export type NivelAcesso = 'nenhum' | 'ver' | 'editar' | 'total';

export interface AcaoModulo {
    chave: string;
    rotulo: string;
}

export interface ModuloPermissao {
    id: string;
    nome: string;
    descricao: string;
    /** O que a permissão do módulo também libera hoje, fora do próprio módulo. */
    tambemLibera?: string;
    acoes: AcaoModulo[];
    /** Módulos sem a escala Ver / Editar / Total: cada ação é marcada à parte. */
    somenteItens?: boolean;
    /** Só aparece para a empresa da plataforma. */
    somentePlataforma?: boolean;
}

export interface AreaPermissao {
    nome: string;
    modulos: ModuloPermissao[];
}

const crud = (prefixo: string): AcaoModulo[] => [
    { chave: `${prefixo}_visualizar`, rotulo: 'Ver' },
    { chave: `${prefixo}_novo`, rotulo: 'Criar' },
    { chave: `${prefixo}_editar`, rotulo: 'Editar' },
    { chave: `${prefixo}_excluir`, rotulo: 'Excluir' },
];

export const AREAS_PERMISSAO: AreaPermissao[] = [
    {
        nome: 'Operação',
        modulos: [
            { id: 'locacao', nome: 'Locações', descricao: 'Contratos de locação, encerramento e renovação.', acoes: crud('locacao') },
            { id: 'vistoria', nome: 'Vistorias', descricao: 'Vistorias de carro e moto e vistoria integrada.', acoes: crud('vistoria') },
            {
                id: 'trocadeoleo', nome: 'Troca de óleo', descricao: 'Aprovação das trocas de óleo enviadas pelos locatários.',
                acoes: [
                    { chave: 'trocadeoleo_visualizar', rotulo: 'Ver' },
                    { chave: 'trocadeoleo_editar', rotulo: 'Editar' },
                    { chave: 'trocadeoleo_excluir', rotulo: 'Excluir' },
                ],
            },
            { id: 'contrato', nome: 'Modelos de contrato', descricao: 'Modelos usados para gerar os contratos.', acoes: crud('contrato') },
            { id: 'locatario', nome: 'Locatários', descricao: 'Cadastro de clientes e documentos.', acoes: crud('locatario') },
            { id: 'veiculo', nome: 'Veículos', descricao: 'Frota, situação e documentos dos veículos.', acoes: crud('veiculo') },
            { id: 'multa', nome: 'Multas', descricao: 'Infrações e repasse ao locatário.', acoes: crud('multa') },
            { id: 'gestao_risco', nome: 'Gestão de risco', descricao: 'Regras de bloqueio automático e ocorrências.', acoes: crud('gestao_risco') },
        ],
    },
    {
        nome: 'Financeiro',
        modulos: [
            { id: 'pagamento', nome: 'Pagamentos', descricao: 'Cobranças das locações, baixas e conciliação.', acoes: crud('pagamento') },
            { id: 'lancamento', nome: 'Lançamentos', descricao: 'Receitas e despesas avulsas.', acoes: crud('lancamento') },
            {
                id: 'nfse', nome: 'Notas fiscais', descricao: 'Consulta, emissão e cancelamento de NFS-e.', somenteItens: true,
                acoes: [
                    { chave: 'nfse_visualizar', rotulo: 'Ver' },
                    { chave: 'nfse_emitir', rotulo: 'Emitir e agendar' },
                    { chave: 'nfse_cancelar', rotulo: 'Cancelar' },
                ],
            },
            { id: 'banco', nome: 'Contas bancárias', descricao: 'Contas usadas nas baixas e nos relatórios.', acoes: crud('banco') },
            { id: 'categoria', nome: 'Categorias', descricao: 'Categorias de receitas e despesas.', acoes: crud('categoria') },
        ],
    },
    {
        nome: 'Frota e manutenção',
        modulos: [
            {
                id: 'servicoveiculo', nome: 'Manutenções', descricao: 'Manutenções, ordens de serviço e plano de manutenção.',
                acoes: crud('servicoveiculo'),
            },
            { id: 'servico', nome: 'Serviços', descricao: 'Tipos de serviço usados nas manutenções.', acoes: crud('servico') },
            { id: 'peca', nome: 'Peças', descricao: 'Cadastro de peças.', acoes: crud('peca') },
            { id: 'prestadorservico', nome: 'Prestadores de serviço', descricao: 'Oficinas e fornecedores.', acoes: crud('prestadorservico') },
            { id: 'estoque', nome: 'Estoque', descricao: 'Entradas, saídas e saldo de peças.', acoes: crud('estoque') },
            {
                id: 'rastreador', nome: 'Rastreador', descricao: 'Mapa, odômetro e bloqueio remoto dos veículos.',
                acoes: [
                    { chave: 'rastreador_visualizar', rotulo: 'Ver' },
                    { chave: 'rastreador_bloquear', rotulo: 'Bloquear e desbloquear' },
                ],
            },
        ],
    },
    {
        nome: 'Gestão',
        modulos: [
            { id: 'dashboard', nome: 'Dashboard', descricao: 'Acesso à tela inicial de indicadores.', acoes: [{ chave: 'dashboard_visualizar', rotulo: 'Ver' }] },
            {
                id: 'dashboard_cards', nome: 'Cards do dashboard', descricao: 'Quais indicadores, gráficos e listas aparecem no dashboard.', somenteItens: true,
                acoes: CARDS_DO_DASHBOARD,
            },
            {
                id: 'relatorio', nome: 'Relatórios', descricao: 'Escolha quais relatórios o usuário pode gerar.', somenteItens: true,
                acoes: [
                    { chave: 'relatorio_locacoes_visualizar', rotulo: 'Locações' },
                    { chave: 'relatorio_veiculos_visualizar', rotulo: 'Veículos' },
                    { chave: 'relatorio_servicoveiculo_visualizar', rotulo: 'Manutenções' },
                    { chave: 'relatorio_despesas_visualizar', rotulo: 'Despesas' },
                    { chave: 'relatorio_receitas_visualizar', rotulo: 'Receitas e fluxo de caixa' },
                ],
            },
            {
                id: 'usuario', nome: 'Usuários', descricao: 'Usuários do sistema.',
                tambemLibera: 'os acessos dos locatários ao app.',
                acoes: crud('usuario'),
            },
            { id: 'filial', nome: 'Unidades', descricao: 'Cadastro e configuração das unidades.', acoes: crud('filial') },
            { id: 'auditoria', nome: 'Auditoria', descricao: 'Histórico de quem fez o quê no sistema.', acoes: [{ chave: 'auditoria_visualizar', rotulo: 'Ver' }] },
            {
                id: 'empresa', nome: 'Empresa', descricao: 'Dados da empresa e configurações que as telas leem ao abrir.',
                acoes: [
                    { chave: 'empresa_visualizar', rotulo: 'Ver' },
                    { chave: 'empresa_editar', rotulo: 'Editar' },
                ],
            },
            {
                id: 'configuracao', nome: 'Configurações', descricao: 'Integrações (Asaas, Autentique, rastreador), NFS-e e mensagens de WhatsApp.',
                somenteItens: true,
                acoes: [{ chave: 'configuracao_editar', rotulo: 'Alterar configurações' }],
            },
            { id: 'cupom', nome: 'Cupons', descricao: 'Cupons de desconto da plataforma.', acoes: crud('cupom'), somentePlataforma: true },
        ],
    },
];

/** Sempre gravada junto, nunca exibida (comportamento mantido do cadastro anterior). */
export const PERMISSAO_OBRIGATORIA = 'pedido_visualizar';

/** Fixas, aplicadas pela API ao salvar (PermissoesFixas): todo usuário vê a afiliação e só o administrador saca. */
export const PERMISSOES_FIXAS = ['afiliacao_visualizar', 'afiliacao_sacar'];

export const ROTULO_NIVEL: Record<NivelAcesso, string> = {
    nenhum: 'Sem acesso',
    ver: 'Ver',
    editar: 'Ver e editar',
    total: 'Total',
};

const DESCRICAO_NIVEL: Record<NivelAcesso, string> = {
    nenhum: 'Não vê o módulo',
    ver: 'Só consulta',
    editar: 'Consulta, cria e edita',
    total: 'Tudo, inclusive excluir',
};

export function descricaoNivel(nivel: NivelAcesso): string {
    return DESCRICAO_NIVEL[nivel];
}

/** Chaves de cada nível do módulo; níveis que dariam o mesmo conjunto do anterior são omitidos. */
export function niveisDoModulo(acoes: AcaoModulo[]): { nivel: NivelAcesso; chaves: string[] }[] {
    const chaves = acoes.map(a => a.chave);
    const ver = chaves.filter(c => c.endsWith('_visualizar'));
    const editar = chaves.filter(c => c.endsWith('_visualizar') || c.endsWith('_novo') || c.endsWith('_editar'));
    const candidatos: { nivel: NivelAcesso; chaves: string[] }[] = [
        { nivel: 'nenhum', chaves: [] },
        { nivel: 'ver', chaves: ver },
        { nivel: 'editar', chaves: editar },
        { nivel: 'total', chaves },
    ];

    const niveis: { nivel: NivelAcesso; chaves: string[] }[] = [];
    candidatos.forEach(c => {
        const anterior = niveis[niveis.length - 1];
        if (c.nivel !== 'nenhum' && c.chaves.length === 0) return;
        if (anterior && anterior.chaves.length === c.chaves.length) return;
        niveis.push(c);
    });
    return niveis;
}

/** Fecha o conjunto com as dependências (transitivas) vindas da API. */
export function completarDependencias(chaves: Iterable<string>, dependencias: Record<string, string[]>): Set<string> {
    const resultado = new Set<string>();
    const pendentes = Array.from(chaves);
    while (pendentes.length > 0) {
        const chave = pendentes.pop() as string;
        if (resultado.has(chave)) continue;
        resultado.add(chave);
        (dependencias[chave] || []).forEach(d => {
            if (!resultado.has(d)) pendentes.push(d);
        });
    }
    return resultado;
}

/** Pontos de partida para preencher a tela; as dependências entram sozinhas ao aplicar. */
export const PERFIS_USUARIO = [
    {
        label: "Atendente",
        valor: "atendente",
        permissoes: [
            "locacao_visualizar", "locacao_novo", "locacao_editar", "locacao_excluir",
            "locatario_visualizar", "locatario_novo", "locatario_editar", "locatario_excluir",
            "veiculo_visualizar", "veiculo_novo", "veiculo_editar", "veiculo_excluir",
            "pagamento_visualizar",
            "lancamento_visualizar",
            "vistoria_visualizar", "vistoria_novo", "vistoria_editar",
            "trocadeoleo_visualizar", "trocadeoleo_editar",
            "contrato_visualizar"
        ]
    },
    {
        label: "Financeiro",
        valor: "financeiro",
        permissoes: [
            "pagamento_visualizar", "pagamento_novo", "pagamento_editar", "pagamento_excluir",
            "lancamento_visualizar", "lancamento_novo", "lancamento_editar", "lancamento_excluir",
            "locacao_visualizar", "veiculo_visualizar",
            "dashboard_visualizar",
            "dashboard_recebimentos_a_vencer", "dashboard_recebimentos_concluidos", "dashboard_recebimentos_em_atraso",
            "dashboard_despesas_resolvidas", "dashboard_despesas_a_vencer", "dashboard_despesas_em_atraso", "dashboard_saldo",
            "dashboard_grafico_faturamento", "dashboard_grafico_custo", "dashboard_grafico_lucro",
            "dashboard_lista_pagamentos", "dashboard_lista_lancamentos", "dashboard_calendario",
            "relatorio_despesas_visualizar", "relatorio_receitas_visualizar",
            "categoria_visualizar", "categoria_novo", "categoria_editar", "categoria_excluir", "empresa_editar", "empresa_visualizar"
        ]
    },
    {
        label: "Mecânico",
        valor: "mecanico",
        permissoes: [
            "servicoveiculo_visualizar", "servicoveiculo_novo", "servicoveiculo_editar", "servicoveiculo_excluir",
            "estoque_visualizar", "peca_visualizar", "veiculo_visualizar", "locacao_visualizar", "servico_visualizar", "servico_editar",
            "servico_novo", "servico_excluir", "peca_novo", "peca_editar", "peca_excluir", "estoque_novo", "estoque_editar", "estoque_excluir"
        ]
    },
    {
        label: "Gerente Operacional",
        valor: "gerente",
        permissoes: [
            "dashboard_visualizar",
            ...CARDS_DO_DASHBOARD.map(c => c.chave),
            "locacao_visualizar", "locacao_novo", "locacao_editar", "locacao_excluir",
            "veiculo_visualizar", "veiculo_novo", "veiculo_editar", "veiculo_excluir",
            "locatario_visualizar", "locatario_novo", "locatario_editar", "locatario_excluir",
            "pagamento_visualizar", "pagamento_novo", "pagamento_editar", "pagamento_excluir",
            "multa_visualizar", "multa_novo", "multa_editar", "multa_excluir",
            "servicoveiculo_visualizar", "servicoveiculo_novo", "servicoveiculo_editar", "servicoveiculo_excluir",
            "estoque_visualizar", "peca_visualizar",
            "relatorio_locacoes_visualizar", "relatorio_veiculos_visualizar",
            "vistoria_visualizar", "vistoria_novo", "vistoria_editar", "vistoria_excluir",
            "trocadeoleo_visualizar", "trocadeoleo_editar", "trocadeoleo_excluir",
            "contrato_visualizar", "contrato_novo", "contrato_editar", "contrato_excluir",
            "rastreador_visualizar", "rastreador_bloquear"
        ]
    }
];

function normalizar(texto: string): string {
    return (texto || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
}

/** Busca do cadastro de usuários: nome, descrição e ações do módulo, sem diferenciar acento e maiúscula. */
export function moduloCorresponde(modulo: { nome: string; descricao: string; acoes: AcaoModulo[] }, busca: string): boolean {
    const termos = normalizar(busca).split(/\s+/).filter(Boolean);
    if (termos.length === 0) return true;
    const texto = normalizar([modulo.nome, modulo.descricao, ...modulo.acoes.map(a => `${a.rotulo} ${a.chave}`)].join(' '));
    return termos.every(t => texto.includes(t));
}
